'use client';

import { type ComponentPropsWithoutRef, type Ref, useState } from 'react';

import classnames from 'classnames';
import { FaShieldHalved } from 'react-icons/fa6';

import type { BaseComponentProps } from '../../base-component.types';
import { Button } from '../../button';
import { IconCounter } from '../../icon-counter';
import { Text } from '../../text';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../tooltip';

import styles from './DateStreakProtectors.module.scss';

type DateStreakProtectorsProps = ComponentPropsWithoutRef<'div'> &
  BaseComponentProps & {
    count: number;
    onRedeem?: () => void;
    loading?: boolean;
    disabled?: boolean;
  };

const PROTECTORS_TOOLTIP_CONTENT = {
  description:
    'Los protectores se activan automáticamente para que tu racha siga intacta',
  info: '1 protector = 25 puntos',
  buttonLabel: 'Añadir protector',
} as const;

export function DateStreakProtectors({
  style,
  className,
  count,
  onRedeem,
  loading = false,
  disabled = false,
  testId = 'ui-date-streak-protectors',
  ref,
  ...rest
}: DateStreakProtectorsProps & { ref?: Ref<HTMLDivElement> }) {
  const [open, setOpen] = useState(false);

  const protectorsContent = (
    <IconCounter
      ref={ref}
      style={style}
      className={classnames(
        styles['date-streak-protectors__icon-counter'],
        className,
        !!onRedeem && styles['date-streak-protectors--clickable'],
      )}
      shadow
      icon={<FaShieldHalved />}
      count={count}
      testId={testId}
      {...rest}
    />
  );

  if (!onRedeem) return protectorsContent;

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger
        onClick={() => setOpen((v) => !v)}
        asChild={true}
        className={styles['date-streak-protectors__trigger']}
      >
        {protectorsContent}
      </TooltipTrigger>
      <TooltipContent className={styles['date-streak-protectors__content']}>
        <span>{PROTECTORS_TOOLTIP_CONTENT.description}</span>
        <Text
          variant="neutral"
          variety="annotation"
          className={styles['date-streak-protectors__text']}
        >
          {PROTECTORS_TOOLTIP_CONTENT.info}
        </Text>
        <Button
          variant="outline-primary"
          onClick={onRedeem}
          className={styles['date-streak-protectors__button']}
          suffixIcon={
            <FaShieldHalved
              className={styles['date-streak-protectors__button__icon']}
              size={18}
            />
          }
          loading={loading}
          disabled={disabled}
        >
          {PROTECTORS_TOOLTIP_CONTENT.buttonLabel}
        </Button>
      </TooltipContent>
    </Tooltip>
  );
}
